import { PropertyValues,property } from "lit-element";
import {ElmLitElement} from '../../Aspect/ElmLitElement';
import {MaybeBool, observeDom} from '../../Aspect/util';
import { initClickAway, InitClickAway } from "./EventfullClickAway";


export {
  defineEventfullElement,
  Evenfull
};

const defineEventfullElement = (): void =>
  customElements.define('event-full', Evenfull);


class Evenfull extends ElmLitElement {
  @property({ type: Number, attribute: 'coef' }) coef = 1;
  @property({ type: Boolean, attribute: 'emit-self-size' }) emitSelfSize = false;
  @property({ type: Boolean, attribute: 'emit-first-child-size' }) emitFirstChildSize = false;
  @property({ type: Boolean, attribute: 'emit-scroll' }) emitScrollNearBottom = false;
  @property({ type: Boolean, attribute: 'emit-scroll-near-top' }) emitScrollNearTop = false;
  @property({ type: Boolean, attribute: 'emit-attach' }) emitAttach = false;
  @property({ type: Boolean, attribute: 'emit-scroll-always' }) emitScrollAlways = false;
  @property({ type: Boolean, attribute: 'emit-mouse-move' }) emitMouseMove = false;
  @property({ type: Boolean, attribute: 'emit-document-mouse-up' }) emitDocumentMouseUp = false;
  @property({ type: Boolean, attribute: 'emit-click-away' }) emitClickAway = false;
  @property({ type: Boolean, attribute: 'emit-click-away-active' }) emitClickAwayActive = false;
  @property({ type: String, attribute: 'prevent-bubble-for-event' }) preventBubbleForEvent: string | undefined;
  @property({ type: String, attribute: 'prevent-wheel-bubble-for-axis' }) preventWheelBubbleForAxis: 'WheelAxisBoth';

  emitsSelfSizeInitialized = false;
  emitsFirstChildSizeInitialized = false;
  observerNearBottom?: MutationObserver;
  observerNearTop?: MutationObserver;
  emitScrollAlwaysInitialized = false;
  emitMouseMoveInitialized = false;
  emitDocumentMouseUpInitialized = false;
  emitClickAwayInitialized?: InitClickAway;
  preventWheelBubbleForAxisInitialized?: Function = undefined;

  attributeChangedCallback(name: string, old: string | null, value: string | null) {
    super.attributeChangedCallback(name, old, value);
    if (value && handlers[name]) {
      handlers[name](this);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    (this.emitSelfSize && handlers['emit-self-size'](this));
    (this.emitFirstChildSize && handlers['emit-first-child-size'](this));
    (this.emitScrollNearBottom && handlers['emit-scroll'](this));
    (this.emitScrollNearTop && handlers['emit-scroll-near-top'](this));
    (this.emitAttach && handlers['emit-attach'](this));
    (this.emitScrollAlways && handlers['emit-scroll-always'](this));
    (this.emitMouseMove && handlers['emit-mouse-move'](this));
    (this.emitDocumentMouseUp && handlers['emit-document-mouse-up'](this));
    (this.emitClickAway && handlers['emit-click-away'](this));
    (this.preventBubbleForEvent && handlers['prevent-bubble-for-event'](this));
    (this.preventWheelBubbleForAxis && handlers['prevent-wheel-bubble-for-axis'](this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observerNearBottom?.disconnect();
    this.observerNearTop?.disconnect();
    this.emitClickAwayInitialized?.disconnect();
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.emitClickAwayInitialized?.updated(_changedProperties);
  }
}





const handlers: { [k: string]: (el: Evenfull) => void } = {
  'emit-self-size': el => el.emitsSelfSizeInitialized ??= initSizeEmitter(el, el),
  'emit-first-child-size': el =>
    (!el.emitsFirstChildSizeInitialized && el.firstChild instanceof HTMLElement)
      ? el.emitsFirstChildSizeInitialized = initSizeEmitter(el, el.firstChild)
      : el.emitsFirstChildSizeInitialized,
  'emit-scroll': el => el.observerNearBottom = handleScrollNearBottom(el),
  'emit-scroll-near-top': el => el.observerNearTop ??= handleScrollNearTop(el),
  'emit-attach': el => requestAnimationFrame(() => el.fire('attached', el, { bubbles: false })),
  'emit-scroll-always': el => el.emitScrollAlwaysInitialized ||= handleScrollAlways(el),
  'emit-mouse-move': el => el.emitMouseMoveInitialized ||= initMouseMoveEmitter(el),
  'emit-document-mouse-up': el => el.emitDocumentMouseUpInitialized ||= initDocumentMouseUp(el),
  'emit-click-away': el => el.emitClickAwayInitialized ??= initClickAway(el),
  'prevent-bubble-for-event': el => el.addEventListener(el.preventBubbleForEvent as string, e => e.stopPropagation()),
  'prevent-wheel-bubble-for-axis': el => el.preventWheelBubbleForAxisInitialized ??= handlePreventWheelBubble(el),
} as const;

function handlePreventWheelBubble(el: Evenfull): Function {
  let mouseIsInside = false;
  el.addEventListener('mouseenter', handleMouseEnter);
  el.addEventListener('mouseleave', handleMouseLeave);
  el.addEventListener('wheel', handleWheel);
  return stopListeners;


  function handleWheel(e: WheelEvent): void {
    if (mouseIsInside) {
      e.stopPropagation();
    }
  }

  function handleMouseEnter(): void {
    if (mouseIsInside) return;
    mouseIsInside = true;
  }

  function handleMouseLeave() {
    mouseIsInside = false;
  }

  function stopListeners(): void {
    el.removeEventListener('mouseenter', handleMouseEnter);
    el.removeEventListener('mouseleave', handleMouseLeave);
    el.removeEventListener('wheel', handleWheel);
  }
}


function handleScrollAlways(el: Evenfull): true {
  el.addEventListener('scroll', () => {
    el.fire('onScroll', { x: el.scrollLeft, y: el.scrollTop });
  });
  return true;
}

function handleScrollNearTop(el: Evenfull): MutationObserver {
  let lastKnownStateWasNearTop: MaybeBool = 'UNKNOWN';
  const observerNearTop = createMutationObserverForScrollNearTopEmitter();
  el.addEventListener('scroll', handleStateForScrollNearTopEmitter);
  handleStateForScrollNearTopEmitter();
  return observerNearTop;

  function createMutationObserverForScrollNearTopEmitter(): MutationObserver {
    const observer = new MutationObserver(handleMutationForScrollNearTopEmitter);
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return observer;
  }

  function handleStateForScrollNearTopEmitter(): void {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (isScrollNearTop(el)) {
          if (lastKnownStateWasNearTop !== true) {
            el.fire('nearTop');
          }
          lastKnownStateWasNearTop = true;
        } else {
          lastKnownStateWasNearTop = false;
        }
      }));
  }

  function handleMutationForScrollNearTopEmitter() {
    lastKnownStateWasNearTop = false;
    handleStateForScrollNearTopEmitter();
  }
}

function handleScrollNearBottom(el: Evenfull): MutationObserver {
  let lastKnownStateWasNearBottom: MaybeBool = 'UNKNOWN';
  const observerNearBottom = createMutationObserverForScrollNearBottomEmitter();
  el.addEventListener('scroll', handleStateForScrollNearBottomEmitter);
  handleStateForScrollNearBottomEmitter();
  return observerNearBottom;

  function createMutationObserverForScrollNearBottomEmitter(): MutationObserver {
    const observer = new MutationObserver(handleMutationForScrollNearBottomEmitter);
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return observer;
  }

  function handleStateForScrollNearBottomEmitter(): void {
    requestAnimationFrame(() => {
      if (isScrollNearBottom(el)) {
        if (lastKnownStateWasNearBottom !== true) {
          el.fire('nearBottom');
        }
        lastKnownStateWasNearBottom = true;
      } else {
        lastKnownStateWasNearBottom = false;
      }
    });
  }

  function handleMutationForScrollNearBottomEmitter() {
    lastKnownStateWasNearBottom = false;
    handleStateForScrollNearBottomEmitter();
  }
}


function initSizeEmitter(thisEl: ElmLitElement, target: HTMLElement): true {
  window.addEventListener('resize', () => requestAnimationFrame(() => fireSize(thisEl, target)));
  requestAnimationFrame(() => fireSize(thisEl, target));
  observeDom(
    target,
    { childList: true, subtree: true },
    { onChildList: _ => requestAnimationFrame(() => fireSize(thisEl, target)) },
  );
  // @ts-ignore
  (ResizeObserver && new ResizeObserver(() => fireSize(thisEl, target)).observe(target));
  return true;
}

function initMouseMoveEmitter(thisEl: ElmLitElement): true {
  thisEl.addEventListener('mousemove', (e) => {
    if (e.offsetY >= 0 && e.offsetY <= thisEl.offsetHeight && e.offsetX >= 0 && e.offsetX <= thisEl.offsetWidth) {
      thisEl.fire('onMouseMoveEvent', {
        x: e.offsetX,
        y: e.offsetY,
        width: thisEl.offsetWidth,
        height: thisEl.offsetHeight,
      });
    }
  });
  return true;
}

function fireSize(thisEl: ElmLitElement, target: HTMLElement): void {
  thisEl.fire('onSize', getSize(target), { bubbles: false });
}

function getSize(el: HTMLElement) {
  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
    contentWidth: el.scrollWidth,
    contentHeight: el.scrollHeight,
  };
}

function isScrollNearBottom(el: Evenfull): boolean {
  //scrollHeight = content height
  //offsetHeight = viewport height
  return el.scrollHeight - el.offsetHeight * el.coef <= el.scrollTop + el.offsetHeight;
}

function isScrollNearTop(el: Evenfull): boolean {
  return el.offsetHeight * el.coef > el.scrollTop;
}

function initDocumentMouseUp(thisEl: Evenfull): true {
  document.body.addEventListener('mouseup', () => { thisEl.fire('onDocumentMouseUp'); });
  return true;
}
